<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { InfoFilled } from '@element-plus/icons-vue'
import { createAnalysis, getAnalysis, openProgressStream, StreamHandle } from '@/api/client'
import ProgressPanel from '@/components/ProgressPanel.vue'
import ReportView from '@/components/ReportView.vue'
import ResearchCards from '@/components/ResearchCards.vue'
import { useAnalysisPersistence, useNotification, type PersistedAnalysis, type CompletedAnalysisRef } from '@/composables/useAnalysisPersistence'
import ValuationBandPanel from '@/components/ValuationBandPanel.vue'

const query = ref('深度分析贵州茅台, 关注估值和资金面')
const depth = ref<'quick' | 'standard' | 'deep'>('standard')
// point-in-time 分析基准日 (ALENS-193): 空 = 实时; 历史日期经后端 fail-closed 校验
const tradeDate = ref<string>('')
const todayStr = new Date().toISOString().slice(0, 10)
// ALENS-192: 服务端 state.skipped_nodes (resume/完成刷新时同步给进度面板)
const skippedNodes = ref<Record<string, string>>({})
function disableFutureDates(d: Date) {
  return d.getTime() > Date.now()
}
const analysisId = ref<string | null>(null)
const loading = ref(false)
const finalReport = ref<any>(null)

type ProgressEvent = { node: string; status: string; detail: string; timestamp: string }

const progressEvents = ref<ProgressEvent[]>([])
const currentNode = ref<string>('')
// SSE 是否处于 live 模式 (历史事件重放完毕、收到 replay_done 之后)。
// 用于区分"回放历史"与"实时进度", 回放期间不刷新当前节点高亮。
const liveMode = ref(false)
let eventSource: StreamHandle | null = null

const persistence = useAnalysisPersistence()
const notifier = useNotification()
const router = useRouter()

function goToThesisWorkbench() {
  const sym = finalReport.value?.intent?.symbol || ''
  router.push({ path: '/thesis', query: sym ? { tab: 'checklist', symbol: sym } : { tab: 'checklist' } })
}

const NODE_TO_DETAIL: Record<string, string> = {
  intent: '正在解析分析意图',
  macro: '正在分析宏观与期货风向',
  fundamental: '正在分析基本面',
  technical: '正在分析技术面',
  debate: '正在进行多空辩论',
  decision: '正在生成投资决策',
  report: '正在生成最终报告'
}

function appendProgressEvent(event: ProgressEvent) {
  const exists = progressEvents.value.some(e =>
    e.node === event.node &&
    e.status === event.status &&
    e.timestamp === event.timestamp
  )
  if (exists) return
  progressEvents.value.push(event)
}

function pushInitEvents(doc: any, events: ProgressEvent[]) {
  if (doc.created_at) {
    events.push({
      node: 'init',
      status: 'started',
      detail: `分析已创建: ${doc.query || ''}`,
      timestamp: doc.created_at
    })
  }

  const contextLog = doc.context_log || []
  if (contextLog.length > 0) {
    const initCompletedTime = contextLog[0].started_at || doc.created_at
    if (initCompletedTime) {
      events.push({
        node: 'init',
        status: 'completed',
        detail: '初始化完成',
        timestamp: initCompletedTime
      })
    }
  }
}

function pushContextLogEvents(doc: any, events: ProgressEvent[]) {
  const contextLog = doc.context_log || []
  for (const entry of contextLog) {
    const name = entry.name
    if (!name) continue
    const detail = NODE_TO_DETAIL[name] || ''

    if (entry.started_at) {
      events.push({
        node: name,
        status: 'started',
        detail: detail,
        timestamp: entry.started_at
      })
    }

    if (entry.ended_at) {
      events.push({
        node: name,
        status: 'completed',
        detail: detail ? `${detail} 完成` : '完成',
        timestamp: entry.ended_at
      })
    }
  }
}

function resolveFailedNode(errorMsg: any): string {
  if (typeof errorMsg === 'string' && errorMsg.includes(':')) {
    const possibleNode = errorMsg.split(':')[0].trim()
    if (NODE_TO_DETAIL[possibleNode] || possibleNode === 'init') {
      return possibleNode
    }
  }
  return 'done'
}

function pushTerminalEvents(doc: any, events: ProgressEvent[]) {
  if (doc.status === 'failed') {
    const errorMsg = doc.error || '未知错误'
    const completedAt = doc.completed_at || doc.updated_at || doc.created_at
    events.push(
      {
        node: resolveFailedNode(errorMsg),
        status: 'failed',
        detail: errorMsg,
        timestamp: completedAt
      },
      {
        node: 'done',
        status: 'failed',
        detail: errorMsg,
        timestamp: completedAt
      }
    )
  } else if (doc.status === 'completed') {
    events.push({
      node: 'done',
      status: 'completed',
      detail: '分析完成',
      timestamp: doc.completed_at || doc.created_at
    })
  }
}

function mapContextLogToProgressEvents(doc: any): ProgressEvent[] {
  const events: ProgressEvent[] = []
  pushInitEvents(doc, events)
  pushContextLogEvents(doc, events)
  pushTerminalEvents(doc, events)
  return events
}

const canSubmit = computed(() => query.value.trim().length > 0 && !loading.value)

const marketHint = computed(() => {
  const q = query.value
  if (/^\d{4,5}\.?HK$/i.test(q) || /hk\.\d+/i.test(q)) return '🇭🇰 港股'
  if (/^[A-Z]{1,5}$/.test(q.trim())) return '🇺🇸 美股'
  if (/BTC|ETH|CRYPTO/i.test(q)) return '💰 加密'
  if (/\b(IF|IC|IM|IH)\b/.test(q)) return '📊 期货'
  if (/\b(sh|sz|bj)\.\d{6}\b/i.test(q) || /^[036]\d{5}$/.test(q.trim())) return '🇨🇳 A股'
  return ''
})

async function handleAnalyze() {
  if (!canSubmit.value) return
  loading.value = true
  progressEvents.value = []
  finalReport.value = null
  currentNode.value = 'init'

  // 请求浏览器通知权限 (仅在用户主动启动分析时)
  await notifier.requestPermission()

  try {
    const resp = await createAnalysis({
      query: query.value,
      depth: depth.value,
      ...(tradeDate.value ? { trade_date: tradeDate.value } : {}),
    })
    analysisId.value = resp.analysis_id
    router.replace({ path: '/', query: { analysis: resp.analysis_id } })
    ElMessage.success(`分析已创建: ${resp.analysis_id.slice(0, 8)}...`)

    // 持久化 (刷新后可恢复)
    persistence.save({
      analysisId: resp.analysis_id,
      query: query.value,
      depth: depth.value,
      startedAt: new Date().toISOString(),
      ...(tradeDate.value ? { tradeDate: tradeDate.value } : {}),
    })

    subscribeToProgress(resp.analysis_id)
  } catch (e: any) {
    ElMessage.error('启动分析失败: ' + (e?.response?.data?.detail || e?.message || e))
    loading.value = false
  }
}

let pollTimer: ReturnType<typeof setInterval> | null = null

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function applyProgressEvents(events: ProgressEvent[]) {
  for (const ev of events) {
    appendProgressEvent(ev)
  }
  const lastEv = events[events.length - 1]
  if (lastEv && lastEv.status === 'started') {
    currentNode.value = lastEv.node
  }
}

function handleAnalysisFailure(error?: string) {
  subscribedAnalysisId = null
  stopPolling()
  notifier.notify('❌ 分析失败', error || '分析发生错误')
  ElMessage.error('分析失败: ' + (error || '未知错误'))
  loading.value = false
  persistence.clear()
  if (eventSource) {
    eventSource.close()
    eventSource = null
  }
}

function handlePollResult(id: string, doc: any) {
  applyProgressEvents(mapContextLogToProgressEvents(doc))
  if (doc.skipped_nodes && typeof doc.skipped_nodes === 'object') {
    skippedNodes.value = doc.skipped_nodes
  }
  if (doc.status === 'completed') {
    stopPolling()
    notifier.notify('✅ 分析完成', `查询: ${query.value.slice(0, 50)}...`)
    fetchFinalResult(id)
  } else if (doc.status === 'failed') {
    handleAnalysisFailure(doc.error)
  }
}

function startPollingFallback(id: string) {
  stopPolling()
  pollTimer = setInterval(async () => {
    if (!loading.value) {
      stopPolling()
      return
    }
    try {
      const doc: any = await getAnalysis(id)
      if (doc) handlePollResult(id, doc)
    } catch (err) {
      console.warn('Polling progress fallback failed:', err)
    }
  }, 2500)
}

// 路由 watcher 与 handleAnalyze 都会调用 subscribeToProgress，用 id 做幂等，
// 避免同一次分析建立两条 SSE (ALENS-107 #12)。
let subscribedAnalysisId: string | null = null

function subscribeToProgress(id: string) {
  if (subscribedAnalysisId === id) return
  subscribedAnalysisId = id
  if (eventSource) {
    eventSource.close()
    eventSource = null
  }
  startPollingFallback(id)

  let errorCount = 0
  let warned = false
  liveMode.value = false
  eventSource = openProgressStream(id, (e) => {
    errorCount = 0
    warned = false
    // SSE 已开始送事件 → 停掉兜底轮询，避免双通道重复请求 (ALENS-107 #3)
    stopPolling()
    appendProgressEvent(e)
    // 回放历史时保持既有节点高亮, 只有 live 事件才推进当前节点
    if (liveMode.value && e.status === 'started') currentNode.value = e.node
    if (e.node === 'done' && e.status === 'completed') {
      stopPolling()
      notifier.notify('✅ 分析完成', `查询: ${query.value.slice(0, 50)}...`)
      fetchFinalResult(id)
    } else if (liveMode.value && e.status === 'failed') {
      handleAnalysisFailure(e.detail)
    }
  }, () => {
    // replay_done: 历史重放完毕, 此后的事件均为实时
    liveMode.value = true
  })

  if (eventSource) {
    eventSource.onerror = () => {
      errorCount++
      if (errorCount < 5) return
      getAnalysis(id)
        .then((doc: any) => {
          if (doc.status === 'running' || doc.status === 'pending' || doc.status === 'queued') {
            if (!warned) {
              console.info('SSE 网络抖动, 后端分析仍在进行, 等待自动恢复')
              warned = true
            }
            return
          }
          if (doc.status === 'completed') {
            stopPolling()
            fetchFinalResult(id)
            return
          }
          if (doc.status === 'failed') {
            stopPolling()
            if (!warned) {
              ElMessage.error('分析已失败: ' + (doc.error || '未知错误'))
              warned = true
            }
            loading.value = false
            persistence.clear()
            if (eventSource) {
              eventSource.close()
              eventSource = null
            }
          }
        })
        .catch(() => {
          if (!warned) {
            ElMessage.warning('与服务器连接中断, 请检查网络')
            warned = true
          }
        })
    }
  }
}

function hydrateRunMeta(doc: any): void {
  // 完成态/路由恢复时回填运行配置 (ALENS-193): 深度驱动进度阶段集合,
  // skipped_nodes 标注短路/续跑复用节点, trade_date 回显 PIT 基准日。
  if (doc?.depth === 'quick' || doc?.depth === 'standard' || doc?.depth === 'deep') {
    depth.value = doc.depth
  }
  if (typeof doc?.trade_date === 'string') {
    tradeDate.value = doc.trade_date
  } else if (doc && doc.trade_date === null) {
    tradeDate.value = ''
  }
  if (doc?.skipped_nodes && typeof doc.skipped_nodes === 'object') {
    skippedNodes.value = doc.skipped_nodes
  }
}

async function fetchFinalResult(id: string) {
  stopPolling()
  try {
    const doc = await getAnalysis(id)
    hydrateRunMeta(doc)
    finalReport.value = doc
    analysisId.value = id
    loading.value = false
    persistence.saveCompleted({
      analysisId: id,
      query: doc.query || query.value,
      completedAt: doc.completed_at || new Date().toISOString(),
    })
    if (eventSource) {
      eventSource.close()
      eventSource = null
    }
  } catch (e) {
    console.warn('获取分析结果失败', e)
    ElMessage.error('获取结果失败')
    loading.value = false
  }
}


function resolveRouteAnalysisId(): string | null {
  if (typeof route.query.analysis === 'string') return route.query.analysis
  if (typeof route.query.id === 'string') return route.query.id
  return null
}

async function resumeFromRoute(routeAnalysisId: string): Promise<boolean> {
  try {
    const doc: any = await getAnalysis(routeAnalysisId)
    analysisId.value = routeAnalysisId
    query.value = doc.query || query.value
    progressEvents.value = mapContextLogToProgressEvents(doc)
    if (doc.status === 'completed') {
      hydrateRunMeta(doc)
      finalReport.value = doc
      loading.value = false
      return true
    }
    if (doc.status === 'failed') {
      loading.value = false
      return true
    }
    loading.value = true
    currentNode.value = progressEvents.value[progressEvents.value.length - 1]?.node || 'init'
    subscribeToProgress(routeAnalysisId)
    return true
  } catch {
    // ignore and continue fallback recovery
    return false
  }
}

function resumeRunningAnalysis(saved: PersistedAnalysis, doc: any) {
  analysisId.value = saved.analysisId
  query.value = saved.query
  depth.value = saved.depth
  tradeDate.value = saved.tradeDate || doc.trade_date || ''
  if (doc.skipped_nodes && typeof doc.skipped_nodes === 'object') {
    skippedNodes.value = doc.skipped_nodes
  }
  loading.value = true
  if (Array.isArray(doc.context_log) && doc.context_log.length > 0) {
    progressEvents.value = mapContextLogToProgressEvents(doc)
    for (let i = progressEvents.value.length - 1; i >= 0; i--) {
      if (progressEvents.value[i].status === 'started') {
        currentNode.value = progressEvents.value[i].node
        break
      }
    }
  } else {
    currentNode.value = 'init'
  }
  ElMessage.info(`恢复未完成的分析: ${saved.analysisId.slice(0, 8)}...`)
  subscribeToProgress(saved.analysisId)
}

async function resumeSavedAnalysis(saved: PersistedAnalysis): Promise<boolean> {
  if (persistence.isStale(saved)) {
    persistence.clear()
    return true
  }
  try {
    // 查后端确认还在 (非 completed/failed)
    const doc: any = await getAnalysis(saved.analysisId)
    if (doc.status === 'completed') {
      // 之前已完成, 直接展示
      hydrateRunMeta(doc)
      finalReport.value = doc
      analysisId.value = saved.analysisId
      ElMessage.info('已恢复上一次分析结果')
      persistence.clear()
      persistence.saveCompleted({
        analysisId: saved.analysisId,
        query: doc.query || saved.query,
        completedAt: doc.completed_at || new Date().toISOString(),
      })
      loading.value = false
      progressEvents.value = mapContextLogToProgressEvents(doc)
      return true
    }
    if (doc.status === 'failed' || doc.status === 'not_found') {
      persistence.clear()
      loading.value = false
      return true
    }
    // running / pending / queued - 预填充历史进度
    resumeRunningAnalysis(saved, doc)
    return true
  } catch (e) {
    console.warn('恢复未完成分析失败', e)
    persistence.clear()
    loading.value = false
    return false
  }
}

async function restoreCompletedAnalysis(completed: CompletedAnalysisRef): Promise<void> {
  try {
    const doc: any = await getAnalysis(completed.analysisId)
    if (doc.status === 'completed') {
      analysisId.value = completed.analysisId
      query.value = doc.query || completed.query || query.value
      hydrateRunMeta(doc)
      finalReport.value = doc
      progressEvents.value = mapContextLogToProgressEvents(doc)
      loading.value = false
    }
  } catch {
    persistence.clearCompleted()
  }
}

async function checkAndResume() {
  const routeAnalysisId = resolveRouteAnalysisId()
  if (routeAnalysisId && (await resumeFromRoute(routeAnalysisId))) return

  const routeSymbol = typeof route.query.symbol === 'string' ? route.query.symbol : null
  if (routeSymbol) {
    query.value = `深度分析 ${routeSymbol}, 关注估值与资金面`
    handleAnalyze()
    return
  }

  // Mount 时检查是否有未完成的分析, 自动恢复
  const saved = persistence.load()
  if (saved && (await resumeSavedAnalysis(saved))) return

  const completed = persistence.loadCompleted()
  if (!completed) return
  await restoreCompletedAnalysis(completed)
}

onMounted(() => {
  checkAndResume()
})

// 监听路由: 仅当切回 HomeView (路径为 / 或带查询参数的 /?...) 时检查恢复。
// 旧条件 startsWith('/') 恒为真, 导致 keep-alive 存活期间任何页面跳转
// 都会触发一次多余的 checkAndResume (重复 getAnalysis 请求)。
const route = useRoute()
watch(
  () => route.fullPath,
  (newPath) => {
    if (newPath === '/' || newPath.startsWith('/?')) {
      checkAndResume()
    }
  },
)

onBeforeUnmount(() => {
  subscribedAnalysisId = null
  stopPolling()
  if (eventSource) {
    eventSource.close()
    eventSource = null
  }
})

</script>

<template>
  <div class="home-view">
    <el-card class="form-card" shadow="hover">
      <template #header>
        <div class="form-header">
          <div>
            <span class="title">🚀 开始单股分析</span>
            <span class="subtitle">输入股票代码或自然语言查询, 调度 6 个智能体协同分析</span>
          </div>
          <div style="display: flex; gap: 8px; align-items: center;">
            <el-button
              type="warning"
              plain
              size="small"
              @click="goToThesisWorkbench"
            >
              📜 价值投资与论文库
            </el-button>
            <el-tag v-if="marketHint" :type="marketHint.includes('A') ? 'danger' : marketHint.includes('港') ? 'success' : marketHint.includes('美') ? 'warning' : 'info'">
              {{ marketHint }}
            </el-tag>
          </div>
        </div>
      </template>
      <el-form @submit.prevent="handleAnalyze">
        <el-form-item label="查询">
          <el-input
            v-model="query"
            type="textarea"
            :rows="3"
            placeholder="例如: 深度分析 sh.600519, 关注估值和资金面  /  分析腾讯控股  /  评估 NVDA"
          />
          <div class="example-tips">
            支持格式:
            <code>sh.600519</code> ·
            <code>0700.HK</code> ·
            <code>AAPL</code> ·
            <code>BTC-USD</code> ·
            <code>IF</code> (期货)
          </div>
        </el-form-item>
        <el-form-item label="深度">
          <el-radio-group v-model="depth">
            <el-radio-button value="quick">⚡ 快速 (1-2 min)</el-radio-button>
            <el-radio-button value="standard">🎯 标准 (3-5 min)</el-radio-button>
            <el-radio-button value="deep">🔬 深度 (5-10 min)</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="基准日">
          <div class="trade-date-row">
            <el-date-picker
              v-model="tradeDate"
              type="date"
              value-format="YYYY-MM-DD"
              placeholder="实时 (不填)"
              :disabled-date="disableFutureDates"
              :clearable="true"
              style="width: 170px"
            />
            <el-tooltip
              content="留空 = 实时模式。填历史日历日 = point-in-time 重放: 工具层按 as-of 扣发实时数据、截断历史窗口、扣发未公告信息; 非法/未来日期将被拒绝 (422), 不会静默降级实时。"
              placement="right"
            >
              <el-tag v-if="tradeDate && tradeDate < todayStr" type="warning" size="small">
                历史 PIT: {{ tradeDate }}
              </el-tag>
              <el-tag v-else-if="tradeDate" type="info" size="small">今日基准</el-tag>
              <el-icon v-else class="hint-icon"><InfoFilled /></el-icon>
            </el-tooltip>
          </div>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="loading" :disabled="!canSubmit" @click="handleAnalyze" size="large">
            启动分析
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <ValuationBandPanel />

    <ProgressPanel
      v-if="progressEvents.length > 0"
      :events="progressEvents"
      :current-node="currentNode"
      :live="liveMode"
      :depth="depth"
      :skipped-nodes="skippedNodes"
    />

    <!-- ALENS-193: 运行配置/数据局限溯源条 (评级/提案/质询正文见 report_md 渲染) -->
    <div v-if="finalReport" class="run-meta">
      <el-tag size="small">深度: {{ finalReport.depth || depth }}</el-tag>
      <el-tag size="small" :type="finalReport.trade_date ? 'warning' : 'info'">
        基准日: {{ finalReport.trade_date || '实时' }}
      </el-tag>
      <el-tag v-if="finalReport.run_signature?.graph_version" size="small" type="info">
        图版本: {{ finalReport.run_signature.graph_version }}
      </el-tag>
      <el-tag
        v-if="finalReport.decision?.data_quality"
        size="small"
        :type="finalReport.decision.data_quality === 'high' ? 'success' : finalReport.decision.data_quality === 'low' ? 'danger' : 'warning'"
      >
        数据质量: {{ finalReport.decision.data_quality }}（局限与明细见报告）
      </el-tag>
    </div>
    <ReportView v-if="finalReport" :report="finalReport" />

    <!-- 深度研究工具箱: 财报抽取 / 舆情 / 流动性 / 蒙特卡洛 -->
    <ResearchCards
      v-if="finalReport"
      :symbol="finalReport?.intent?.symbol || null"
      :initial-research="finalReport?.deep_research || null"
    />
  </div>
</template>

<style scoped>
.home-view { display: flex; flex-direction: column; gap: 16px; }
.form-header { display: flex; justify-content: space-between; align-items: flex-start; }
.title { font-size: 18px; font-weight: 600; display: block; color: var(--text-primary); }
.subtitle { font-size: 13px; color: var(--text-secondary); display: block; margin-top: 2px; }
.example-tips { font-size: 12px; color: var(--text-muted); margin-top: 6px; }
.example-tips code { background: var(--code-bg); color: var(--code-color); padding: 2px 6px; border-radius: 4px; margin: 0 2px; border: 1px solid var(--border-color); }
.run-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 0;
}
.hint-icon {
  margin-left: 6px;
  color: var(--text-secondary, #909399);
}
.trade-date-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
